import * as OperationTypes from '../../OperationTypes';
import * as OperatorTypes from '../../ObjectSegOperatorTypes';
import { DATE, DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import invariant from 'react-utils/invariant';
import { convertDateWithoutYearToIndexedTimePointForAnniversaryDate, convertMomentDateToTimePoint, convertMomentDateWithTimeToTimePoint } from '../../timeFilters/timeFiltersUtils';
import * as TimeOperationEndpointBehaviorTypes from '../../timeFilters/TimeOperationEndpointBehaviorTypes';
import * as TimePointTimeTypes from '../../timeFilters/TimePointTimeTypes';
import * as IndexedTimePointReferenceTypes from '../../timeFilters/IndexedTimePointReferenceTypes';
import * as DateTimePointReferenceTypes from '../../timeFilters/DateTimePointReferenceTypes';
import * as TimeOperationPropertyParserTypes from '../../timeFilters/TimeOperationPropertyParserTypes';
import { hasRemoveTimezoneForDates } from '../../../../permissions/gates';
import { formatDurationFromMillisecondsToTimepoint } from '../../../../utils/durationUtilities';
import I18n from 'I18n';
import { TIME_SINCE } from 'customer-data-objects/property/DateDisplayHint';
import Less from '../../../../filterQueryFormat/operator/Less';
import LessOrEqual from '../../../../filterQueryFormat/operator/LessOrEqual';
import Greater from '../../../../filterQueryFormat/operator/Greater';
import GreaterOrEqual from '../../../../filterQueryFormat/operator/GreaterOrEqual';
import NotEqual from '../../../../filterQueryFormat/operator/NotEqual';
import Equal from '../../../../filterQueryFormat/operator/Equal';
import { isValidDateWithoutYear } from '../../../../components/operator/AnniversaryDateInput';
export const getEqualToDateOperationWithTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getEqualToDateOperationWithoutTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  if (operator.field && operator.field.type === DATE_TIME) {
    return getEqualToDateOperationWithTimezones(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_BETWEEN,
      lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
      upperBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'end'),
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};

// handles 1. Equal to and 2. Not equal to
export const getRelativeTimeOperationFromEqualOrNotEqual = operator => {
  var _operator$field;
  const {
    value
  } = operator;
  const dateDisplayHint = (_operator$field = operator.field) === null || _operator$field === void 0 ? void 0 : _operator$field.dateDisplayHint;
  const timePointOffset = formatDurationFromMillisecondsToTimepoint(value);
  if (!timePointOffset) {
    return invariant(false, `${value} is not a valid relative date value`);
  }
  const {
    days
  } = timePointOffset;
  const filterOperator = operator.constructor === Equal ? OperatorTypes.IS_BETWEEN : OperatorTypes.IS_NOT_BETWEEN;
  const renderSpec = JSON.stringify({
    relativeTimeCalculationType: dateDisplayHint
  });
  if (dateDisplayHint === TIME_SINCE) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
      propertyType: OperationTypes.RANGED_TIME,
      operator: filterOperator,
      renderSpec,
      lowerBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: -days
        },
        zoneId: I18n.moment.portalTz().tz() || 'UTC'
      },
      upperBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: -days
        },
        zoneId: I18n.moment.portalTz().tz() || 'UTC'
      },
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  } else {
    // dateDisplayHint === TIME_UNTIL
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
      propertyType: OperationTypes.RANGED_TIME,
      operator: filterOperator,
      renderSpec,
      lowerBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days
        },
        zoneId: I18n.moment.portalTz().tz() || 'UTC'
      },
      upperBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days
        },
        zoneId: I18n.moment.portalTz().tz() || 'UTC'
      },
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
};
export const getEqualToDateOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getEqualToDateOperationWithoutTimezones(operator);
  } else {
    return getEqualToDateOperationWithTimezones(operator);
  }
};

// handles 1. Less than and 2. Less than or equal to
export const getRelativeTimeOperationFromLess = operator => {
  var _operator$field2, _operator$field3;
  const {
    value
  } = operator;
  const dateDisplayHint = (_operator$field2 = operator.field) === null || _operator$field2 === void 0 ? void 0 : _operator$field2.dateDisplayHint;
  const isDateField = ((_operator$field3 = operator.field) === null || _operator$field3 === void 0 ? void 0 : _operator$field3.type) === DATE;
  const referenceType = isDateField ? IndexedTimePointReferenceTypes.TODAY : IndexedTimePointReferenceTypes.NOW;
  const propertyParser = isDateField ? TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION : TimeOperationPropertyParserTypes.VALUE;
  const timePointOffset = formatDurationFromMillisecondsToTimepoint(value);
  if (!timePointOffset) {
    return invariant(false, `${value} is not a valid relative date value`);
  }
  const {
    days,
    hours,
    minutes,
    seconds
  } = timePointOffset;
  const endpointBehavior = operator.constructor === LessOrEqual ? TimeOperationEndpointBehaviorTypes.INCLUSIVE : TimeOperationEndpointBehaviorTypes.EXCLUSIVE;
  const renderSpec = JSON.stringify({
    relativeTimeCalculationType: dateDisplayHint
  });
  if (dateDisplayHint === TIME_SINCE) {
    return {
      propertyParser,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_AFTER,
      renderSpec,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType
        },
        offset: {
          days: -days,
          hours: -hours,
          minutes: -minutes,
          seconds: -seconds
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior
    };
  } else {
    // dateDisplayHint === TIME_UNTIL
    return {
      propertyParser,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_BEFORE,
      renderSpec,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType
        },
        offset: {
          days,
          hours,
          minutes,
          seconds
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior
    };
  }
};

// handles 1. Greater than and 2. Greater than or equal to
export const getRelativeTimeOperationFromGreater = operator => {
  var _operator$field4, _operator$field5;
  const {
    value
  } = operator;
  const dateDisplayHint = (_operator$field4 = operator.field) === null || _operator$field4 === void 0 ? void 0 : _operator$field4.dateDisplayHint;
  const isDateField = ((_operator$field5 = operator.field) === null || _operator$field5 === void 0 ? void 0 : _operator$field5.type) === DATE;
  const referenceType = isDateField ? IndexedTimePointReferenceTypes.TODAY : IndexedTimePointReferenceTypes.NOW;
  const propertyParser = isDateField ? TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION : TimeOperationPropertyParserTypes.VALUE;
  const timePointOffset = formatDurationFromMillisecondsToTimepoint(value);
  if (!timePointOffset) {
    return invariant(false, `${value} is not a valid relative date value`);
  }
  const {
    days,
    hours,
    minutes,
    seconds
  } = timePointOffset;
  const endpointBehavior = operator.constructor === GreaterOrEqual ? TimeOperationEndpointBehaviorTypes.INCLUSIVE : TimeOperationEndpointBehaviorTypes.EXCLUSIVE;
  const renderSpec = JSON.stringify({
    relativeTimeCalculationType: dateDisplayHint
  });
  if (dateDisplayHint === TIME_SINCE) {
    return {
      propertyParser,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_BEFORE,
      renderSpec,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType
        },
        offset: {
          days: -days,
          hours: -hours,
          minutes: -minutes,
          seconds: -seconds
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior
    };
    // dateDisplayHint === TIME_UNTIL
  } else {
    return {
      propertyParser,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_AFTER,
      renderSpec,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType
        },
        offset: {
          days,
          hours,
          minutes,
          seconds
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior
    };
  }
};
export const getRelativeTimeOperation = operator => {
  if (operator.constructor === Less || operator.constructor === LessOrEqual) {
    return getRelativeTimeOperationFromLess(operator);
  } else if (operator.constructor === Greater || operator.constructor === GreaterOrEqual) {
    return getRelativeTimeOperationFromGreater(operator);
  } else if (operator.constructor === Equal || operator.constructor === NotEqual) {
    return getRelativeTimeOperationFromEqualOrNotEqual(operator);
  }
  return invariant(false, `${operator.constructor.toString()} is not a valid operator`);
};
export const getAfterDateOperationWithTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: convertMomentDateToTimePoint(operator.value, 'end'),
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getAfterDateOperationWithoutTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  if (operator.field && operator.field.type === DATE_TIME) {
    return getAfterDateOperationWithTimezones(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_AFTER,
      timePoint: convertMomentDateToTimePoint(operator.value, 'end'),
      endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
    };
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: convertMomentDateToTimePoint(operator.value, 'end'),
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getAfterDateOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getAfterDateOperationWithoutTimezones(operator);
  } else {
    return getAfterDateOperationWithTimezones(operator);
  }
};
export const getAfterDateWithTimeOperation = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: convertMomentDateWithTimeToTimePoint(operator.value),
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getBeforeDateOperationWithTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: convertMomentDateToTimePoint(operator.value, 'start')
  };
};
export const getBeforeDateOperationWithoutTimezones = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  if (operator.field && operator.field.type === DATE_TIME) {
    return getBeforeDateOperationWithTimezones(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_BEFORE,
      timePoint: convertMomentDateToTimePoint(operator.value, 'start')
    };
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: convertMomentDateToTimePoint(operator.value, 'start')
  };
};
export const getBeforeDateOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getBeforeDateOperationWithoutTimezones(operator);
  } else {
    return getBeforeDateOperationWithTimezones(operator);
  }
};
export const getBeforeDateWithTimeOperation = operator => {
  if (!I18n.moment.isMoment(operator.value)) {
    return invariant(false, `${operator.value} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: convertMomentDateWithTimeToTimePoint(operator.value)
  };
};
export const getBetweenDatesWithTimezonesOperation = operator => {
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (ignoreYear) {
    if (!isValidDateWithoutYear(operator.value) || !isValidDateWithoutYear(operator.highValue)) {
      return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid anniversary date value`);
    }
    return {
      propertyParser: TimeOperationPropertyParserTypes.ANNIVERSARY,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_BETWEEN,
      lowerBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.value, 'start'),
      upperBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.highValue, 'end'),
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.highValue, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getBetweenDatesWithoutTimezonesOperation = operator => {
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones || operator.field && operator.field.type === DATE_TIME) {
    return getBetweenDatesWithTimezonesOperation(operator);
  }
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (ignoreYear) {
    if (!isValidDateWithoutYear(operator.value) || !isValidDateWithoutYear(operator.highValue)) {
      return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid anniversary date value`);
    }
    return {
      propertyParser: TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_BETWEEN,
      lowerBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.value, 'start'),
      upperBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.highValue, 'end'),
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.highValue, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getBetweenDatesOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getBetweenDatesWithoutTimezonesOperation(operator);
  } else {
    return getBetweenDatesWithTimezonesOperation(operator);
  }
};
export const getBetweenDatesWithTimeOperation = operator => {
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: convertMomentDateWithTimeToTimePoint(operator.value),
    upperBoundTimePoint: convertMomentDateWithTimeToTimePoint(operator.highValue),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getNotBetweenDatesWithTimezonesOperation = operator => {
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (ignoreYear) {
    if (!isValidDateWithoutYear(operator.value) || !isValidDateWithoutYear(operator.highValue)) {
      return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid anniversary date value`);
    }
    return {
      propertyParser: TimeOperationPropertyParserTypes.ANNIVERSARY,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_NOT_BETWEEN,
      lowerBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.value, 'start'),
      upperBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.highValue, 'end'),
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_NOT_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.highValue, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getNotBetweenDatesWithoutTimezonesOperation = operator => {
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones || operator.field && operator.field.type === DATE_TIME) {
    return getNotBetweenDatesWithTimezonesOperation(operator);
  }
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (ignoreYear) {
    if (!isValidDateWithoutYear(operator.value) || !isValidDateWithoutYear(operator.highValue)) {
      return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid anniversary date value`);
    }
    return {
      propertyParser: TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_NOT_BETWEEN,
      lowerBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.value, 'start'),
      upperBoundTimePoint: convertDateWithoutYearToIndexedTimePointForAnniversaryDate(operator.highValue, 'end'),
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_NOT_BETWEEN,
    lowerBoundTimePoint: convertMomentDateToTimePoint(operator.value, 'start'),
    upperBoundTimePoint: convertMomentDateToTimePoint(operator.highValue, 'end'),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getNotBetweenDatesOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getNotBetweenDatesWithoutTimezonesOperation(operator);
  } else {
    return getNotBetweenDatesWithTimezonesOperation(operator);
  }
};
export const getNotBetweenDatesWithTimeOperation = operator => {
  if (!I18n.moment.isMoment(operator.value) || !I18n.moment.isMoment(operator.highValue)) {
    return invariant(false, `value ${operator.value} or highValue ${operator.highValue} is not a valid date value`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_NOT_BETWEEN,
    lowerBoundTimePoint: convertMomentDateWithTimeToTimePoint(operator.value),
    upperBoundTimePoint: convertMomentDateWithTimeToTimePoint(operator.highValue),
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
const getMoreThanXDaysAgoWithTimezonesOperation = operator => {
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -operator.numberOfDays
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
const getMoreThanXDaysAgoWithoutTimezonesOperation = operator => {
  if (operator.field && operator.field.type === DATE_TIME) {
    return getMoreThanXDaysAgoWithTimezonesOperation(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_BEFORE,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: -operator.numberOfDays
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
    };
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -operator.numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
const getMoreThanXDaysAgoOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getMoreThanXDaysAgoWithoutTimezonesOperation(operator);
  } else {
    return getMoreThanXDaysAgoWithTimezonesOperation(operator);
  }
};
const getMoreThanXDaysFromNowWithTimezonesOperation = operator => {
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: operator.numberOfDays
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
const getMoreThanXDaysFromNowWithoutTimezonesOperation = operator => {
  if (operator.field && operator.field.type === DATE_TIME) {
    return getMoreThanXDaysFromNowWithTimezonesOperation(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  if (hasTimezones) {
    return {
      propertyParser: TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.TIME_POINT,
      operator: OperatorTypes.IS_AFTER,
      timePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: operator.numberOfDays
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
    };
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: operator.numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
const getMoreThanXDaysFromNowOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getMoreThanXDaysFromNowWithoutTimezonesOperation(operator);
  } else {
    return getMoreThanXDaysFromNowWithTimezonesOperation(operator);
  }
};
export const getMoreThanXDaysOperation = (operator, options) => {
  const numberOfDays = operator.numberOfDays;
  if (typeof numberOfDays !== 'number' || Number.isNaN(numberOfDays)) {
    return invariant(false, `${numberOfDays} is not a valid number of days for ${operator.constructor.name} operator`);
  }
  const direction = operator.direction;
  if (direction === 'backward') {
    return getMoreThanXDaysAgoOperation(operator, options);
  } else if (direction === 'forward') {
    return getMoreThanXDaysFromNowOperation(operator, options);
  }
  return invariant(false, `${direction} is not a valid direction for ${operator.constructor.name} operator`);
};
const getLessThanXDaysAgoWithTimezonesOperation = operator => {
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  return {
    propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY : TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -operator.numberOfDays
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
const getLessThanXDaysAgoWithoutTimezonesOperation = operator => {
  if (operator.field && operator.field.type === DATE_TIME) {
    return getLessThanXDaysAgoWithTimezonesOperation(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (hasTimezones) {
    return {
      propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY : TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_BETWEEN,
      lowerBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: -operator.numberOfDays
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      upperBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.NOW
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  return {
    propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION : TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -operator.numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
const getLessThanXDaysAgoOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getLessThanXDaysAgoWithoutTimezonesOperation(operator);
  } else {
    return getLessThanXDaysAgoWithTimezonesOperation(operator);
  }
};
const getLessThanXDaysFromNowWithTimezonesOperation = operator => {
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  return {
    propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY : TimeOperationPropertyParserTypes.VALUE,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: operator.numberOfDays
      },
      zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
const getLessThanXDaysFromNowWithoutTimezonesOperation = operator => {
  if (operator.field && operator.field.type === DATE_TIME) {
    return getLessThanXDaysFromNowWithTimezonesOperation(operator);
  }
  const hasTimezones = operator && operator.hasTimezones;
  const ignoreYear = operator === null || operator === void 0 ? void 0 : operator.ignoreYear;
  if (hasTimezones) {
    return {
      propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY : TimeOperationPropertyParserTypes.VALUE,
      propertyType: OperationTypes.RANGED_TIME,
      operator: OperatorTypes.IS_BETWEEN,
      lowerBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.NOW
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      upperBoundTimePoint: {
        timeType: TimePointTimeTypes.INDEXED,
        indexReference: {
          referenceType: IndexedTimePointReferenceTypes.TODAY
        },
        offset: {
          days: operator.numberOfDays
        },
        zoneId: operator.zoneId || I18n.moment.portalTz().tz() || 'UTC'
      },
      lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
      upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
    };
  }
  return {
    propertyParser: ignoreYear ? TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION : TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: operator.numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
const getLessThanXDaysFromNowOperation = (operator, options) => {
  if (hasRemoveTimezoneForDates(options.isUngated)) {
    return getLessThanXDaysFromNowWithoutTimezonesOperation(operator);
  } else {
    return getLessThanXDaysFromNowWithTimezonesOperation(operator);
  }
};
export const getLessThanXDaysOperation = (operator, options) => {
  const numberOfDays = operator.numberOfDays;
  if (typeof numberOfDays !== 'number' || Number.isNaN(numberOfDays)) {
    return invariant(false, `${numberOfDays} is not a valid number of days for ${operator.constructor.name} operator`);
  }
  if (operator.direction === 'backward') {
    return getLessThanXDaysAgoOperation(operator, options);
  } else if (operator.direction === 'forward') {
    return getLessThanXDaysFromNowOperation(operator, options);
  }
  return invariant(false, `${operator.direction} is not a valid direction for ${operator.constructor.name} operator`);
};
export const getAfterAnotherPropertyOperation = operator => {
  var _operator$field6;
  if (typeof operator.value !== 'string') {
    return invariant(false, `${operator.value} is not a valid property name`);
  }
  const referenceType = operator.referencedPropertyType === DATE ? DateTimePointReferenceTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION : DateTimePointReferenceTypes.VALUE;
  const propertyIsDateTime = ((_operator$field6 = operator.field) === null || _operator$field6 === void 0 ? void 0 : _operator$field6.type) === DATE_TIME;
  return {
    propertyParser: propertyIsDateTime ? TimeOperationPropertyParserTypes.VALUE : TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: {
      timeType: TimePointTimeTypes.PROPERTY_REFERENCED,
      property: operator.value,
      referenceType,
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getBeforeAnotherPropertyOperation = operator => {
  var _operator$field7;
  if (typeof operator.value !== 'string') {
    return invariant(false, `${operator.value} is not a valid property name`);
  }
  const referenceType = operator.referencedPropertyType === DATE ? DateTimePointReferenceTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION : DateTimePointReferenceTypes.VALUE;
  const propertyIsDateTime = ((_operator$field7 = operator.field) === null || _operator$field7 === void 0 ? void 0 : _operator$field7.type) === DATE_TIME;
  return {
    propertyParser: propertyIsDateTime ? TimeOperationPropertyParserTypes.VALUE : TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: {
      timeType: TimePointTimeTypes.PROPERTY_REFERENCED,
      property: operator.value,
      referenceType,
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getPropertyUpdatedInLastXDaysOperation = operator => {
  const numberOfDays = operator.numberOfDays;
  if (typeof numberOfDays !== 'number' || Number.isNaN(numberOfDays)) {
    return invariant(false, `${numberOfDays} is not a valid number of days for ${operator.name} operator`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.UPDATED_AT,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getPropertyNotUpdatedInLastXDaysOperation = operator => {
  const numberOfDays = operator.numberOfDays;
  if (typeof numberOfDays !== 'number' || Number.isNaN(numberOfDays)) {
    return invariant(false, `${numberOfDays} is not a valid number of days for ${operator.name} operator`);
  }
  return {
    propertyParser: DateTimePointReferenceTypes.UPDATED_AT,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_NOT_BETWEEN,
    lowerBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.TODAY
      },
      offset: {
        days: -numberOfDays
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    upperBoundTimePoint: {
      timeType: TimePointTimeTypes.INDEXED,
      indexReference: {
        referenceType: IndexedTimePointReferenceTypes.NOW
      },
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    lowerBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE,
    upperBoundEndpointBehavior: TimeOperationEndpointBehaviorTypes.INCLUSIVE
  };
};
export const getUpdatedAfterPropertyOperation = operator => {
  if (typeof operator.value !== 'string') {
    return invariant(false, `${operator.value} is not a valid property name`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.UPDATED_AT,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_AFTER,
    timePoint: {
      timeType: TimePointTimeTypes.PROPERTY_REFERENCED,
      property: operator.value,
      referenceType: DateTimePointReferenceTypes.UPDATED_AT,
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};
export const getUpdatedBeforePropertyOperation = operator => {
  if (typeof operator.value !== 'string') {
    return invariant(false, `${operator.value} is not a valid property name`);
  }
  return {
    propertyParser: TimeOperationPropertyParserTypes.UPDATED_AT,
    propertyType: OperationTypes.TIME_POINT,
    operator: OperatorTypes.IS_BEFORE,
    timePoint: {
      timeType: TimePointTimeTypes.PROPERTY_REFERENCED,
      property: operator.value,
      referenceType: DateTimePointReferenceTypes.UPDATED_AT,
      zoneId: I18n.moment.portalTz().tz() || 'UTC'
    },
    endpointBehavior: TimeOperationEndpointBehaviorTypes.EXCLUSIVE
  };
};